import React, {
	createContext,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactElement,
} from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useReadOnlyAccess } from "../hooks/useReadOnlyAccess";
import { AnalysisActions } from "../redux/analysis/analysis.slice";
import {
	getPointByRunIdThunk,
	getQuestionsForAnalysisThunk,
} from "../redux/analysis/analysis.thunk";
import {
	useAppDispatch,
	useAppSelector,
} from "../redux/store";
import { services } from "../services";
import { AnalysisStageEnum } from "../types/analysis.type";
import { subscribeToLedgerPaulTurnComplete } from "../utils/ledgerPaulEvents";
import { hashUserIdToColor } from "../utils/tenantUtils";

export const AnalysisContext = createContext<any>(null);

export const AnalysisContextProvider: React.FC<{
	children: ReactElement;
	datasetIdOverride?: string;
	onSelectedPointChange?: (point: { pointId: string; label: string; editable: boolean } | null) => void;
}> = ({ children, datasetIdOverride, onSelectedPointChange }) => {
	const dispatch = useAppDispatch();
	const { valuationRun, pointData, jsonParameters } =
		useAppSelector((state) => state.analysis);

	const { user } = useAppSelector((state) => state.user);
	const isReadOnlyUser = useReadOnlyAccess();

	const { tapeId: routeTapeId } = useParams<{ tapeId: string }>();
	const tapeId = datasetIdOverride ?? routeTapeId;
	const isDatasetValuation = Boolean(datasetIdOverride);
	const [selectedRun, setSelectedRun] = useState<Record<
		string,
		any
	> | null>(null);

	const [selectedUID, setSelectedUID] = useState<
		string | null
	>(null);

	const [selectedUser, setSelectedUser] =
		useState<any>(null);

	const [isRunEditable, setIsRunEditable] = useState(false);

	useEffect(() => {
		if (isReadOnlyUser) {
			setIsRunEditable(false);
		} else if (selectedUser) {
			setIsRunEditable(selectedUser.is_editable);
		} else {
			setIsRunEditable(false);
		}
	}, [isReadOnlyUser, selectedUser]);

	const [selectedPoint, setSelectedPoint] =
		useState<any>(null);

	useEffect(() => {
		onSelectedPointChange?.(selectedPoint
			? { pointId: selectedPoint.pointId, label: selectedPoint.label, editable: isRunEditable }
			: null);
	}, [onSelectedPointChange, selectedPoint, isRunEditable]);

	const controller = useRef<any>({});
	const timeout = useRef<NodeJS.Timeout | null>(null);
	const continuePolling = useRef(true);

	const resetEverything = useCallback(() => {
		Object.keys(controller.current).forEach((key) => {
			controller.current[key].abort();
		});
		if (timeout.current) {
			clearTimeout(timeout.current);
		}
		controller.current = {};
		timeout.current = null;
		continuePolling.current = false;
	}, []);

	useEffect(() => {
		return () => {
			resetEverything();
			dispatch(AnalysisActions.resetAnalysis());
		};
	}, [dispatch, resetEverything]);

	useEffect(() => {
		if (valuationRun.loading) {
			resetEverything();
			return;
		}
		resetEverything();
		setSelectedPoint(null);
		setSelectedAction(null);
		const runIsAvailable = valuationRun.data.some((run) =>
			run.users.some((runUser: any) => runUser.run_id === selectedRun?.runId)
		);
		if (selectedRun?.runId && tapeId && runIsAvailable) {
			void dispatch(
				getPointByRunIdThunk({
					runId: selectedRun.runId,
					tapeId,
					dataset: isDatasetValuation,
				})
			);
		} else {
			dispatch(AnalysisActions.clearSelectedRun());
		}
	}, [
		dispatch,
		isDatasetValuation,
		resetEverything,
		selectedRun?.runId,
		tapeId,
		valuationRun.data,
		valuationRun.loading,
	]);

	const [
		isEditMaestroDrawerOpen,
		setIsEditMaestroDrawerOpen,
	] = useState(false);

	const handlePolling = useCallback(
		async (pointId: string, requestController: AbortController) => {
			if (!pointId || !tapeId) return;
			try {
				if (requestController.signal.aborted) return;
				dispatch(AnalysisActions.updatePointData(null));
				dispatch(
					AnalysisActions.togglePointDataLoading(true)
				);
				const res =
					await services.analysisServiceInstance.getPointData(
						pointId,
						tapeId,
						{
							signal: requestController.signal,
						},
						isDatasetValuation
					);
				if (requestController.signal.aborted) return;
				if (res.status === AnalysisStageEnum.COMPLETED) {
					dispatch(AnalysisActions.updatePointData(res));
				} else if (
					[
						AnalysisStageEnum.ERROR,
						AnalysisStageEnum.PRICING_FAILED,
						AnalysisStageEnum.CALIPER_RUN_FAILED,
						AnalysisStageEnum.SEND_EMAIL_FAILED,
						AnalysisStageEnum.TAPE_SANITIZATION_FAILED,
						AnalysisStageEnum.CASH_FLOW_GENERATION_FAILED,
					].includes(res.status)
				) {
					continuePolling.current = false;
					dispatch(AnalysisActions.updatePointData(res));
					dispatch(
						AnalysisActions.updatePointWithError({
							error_code: res.status,
							msg: res.error_msg || "An error occurred",
						})
					);
				} else {
					timeout.current = setTimeout(() => {
						continuePolling.current = true;
						void handlePolling(pointId, requestController);
					}, 5000);
				}
			} catch (error: any) {
				if (requestController.signal.aborted) return;
				console.error("handling polling", error);
				continuePolling.current = false;
				// dispatch(
				// 	AnalysisActions.togglePointDataLoading(false)
				// );
				dispatch(
					AnalysisActions.updatePointWithError(error?.data)
				);
			}
		},
		[tapeId, isDatasetValuation, dispatch]
	);

	const handlePointSelection = useCallback(
		(point: any) => {
			if (tapeId) {
				resetEverything();
				const pointId = point.pointId;
				const requestController = new AbortController();
				controller.current[pointId] = requestController;
				dispatch(AnalysisActions.clearSelectedPoint());
				setSelectedPoint(point);
				setSelectedAction(null);
				void handlePolling(pointId, requestController);
			}
		},
		[tapeId, resetEverything, handlePolling, dispatch]
	);

	const reloadPointData = useCallback(
		(pointId: string) => {
			if (selectedPoint?.pointId === pointId) {
				if (controller.current[pointId]) {
					controller.current[pointId].abort();
					clearTimeout(timeout.current as NodeJS.Timeout);
				}
				const requestController = new AbortController();
				controller.current[pointId] = requestController;
				void handlePolling(pointId, requestController);
				void dispatch(
					getQuestionsForAnalysisThunk(
						selectedPoint.pointId
					)
				);
			}
		},
		[dispatch, handlePolling, selectedPoint?.pointId]
	);

	useEffect(() => {
		if (!isDatasetValuation || !tapeId) return;
		return subscribeToLedgerPaulTurnComplete(tapeId, () => {
			if (selectedPoint?.pointId) {
				void dispatch(getQuestionsForAnalysisThunk(selectedPoint.pointId));
			}
		});
	}, [dispatch, isDatasetValuation, selectedPoint?.pointId, tapeId]);

	useEffect(() => {
		if (selectedPoint) {
			void dispatch(
				getQuestionsForAnalysisThunk(selectedPoint.pointId)
			);
		}
	}, [dispatch, resetEverything, selectedPoint]);

	const resetPointData = useCallback(async () => {
		if (isReadOnlyUser) {
			return;
		}
		if (selectedPoint?.pointId) {
			try {
				const data =
					await services.analysisServiceInstance.resetPointData(
						selectedPoint.pointId
					);
				if (data.is_success) {
					toast.success("Point data reset successfully");
					reloadPointData(selectedPoint.pointId);
				} else {
					toast.error(data.error_msg);
				}
			} catch (error) {
				console.error("resetting point data", error);
			}
		}
	}, [
		isReadOnlyUser,
		reloadPointData,
		selectedPoint?.pointId,
	]);

	const [selectedAction, setSelectedAction] = useState<{
		label: string;
		value: string;
	} | null>(null);

	const actionOptions = useMemo(() => {
		if (pointData.data?.graphs?.actions) {
			const temp = pointData.data.graphs.actions;
			const res = temp.map(
				(action: {
					action_id: string;
					action_name: string;
					probs: Array<number>;
				}) => {
					return {
						actionId: action.action_id,
						actionName: action.action_name,
						data: action.probs,
					};
				}
			);
			return res;
		} else if (jsonParameters.data?.actions) {
			const temp = jsonParameters.data.actions;
			const res = temp.map((action: any) => {
				return {
					actionId: action.meta.actionId,
					actionName: action.meta.displayName,
					data: [],
				};
			});
			return res;
		} else return [];
	}, [pointData.data?.graphs?.actions, jsonParameters.data?.actions]);

	useEffect(() => {
		if (actionOptions.length > 0 && !selectedAction) {
			setSelectedAction({
				label: actionOptions[0].actionName,
				value: actionOptions[0].actionId,
			});
		}
	}, [actionOptions, selectedAction]);

	const [modelOptions, setModelOptions] = useState<
		Array<any>
	>([]);

	const [userList, setUserList] = useState<Array<any>>([]);

	useEffect(() => {
		if (!user?.uid || valuationRun.loading) return;
		const uid = selectedUser
			? selectedUser.initiated_by_uid
			: user?.uid;

		const userList: Array<any> = [];
		const runs = valuationRun.data.map((run) => {
			const obj = {
				runName: run.run_data.run_type_name,
				runId: "",
				runCombinationId: run.run_data.run_combination_id,
			};

			userList.push({
				runCombinationId: run.run_data.run_combination_id,
				users: run.users.map((el: any) => {
					const name = el.run_name.split(" ");
					return {
						...el,
						tapeUserIcon:
							name.length > 1
								? name[0][0] + name[1][0]
								: name[0][0],
						tapeUserIconColor: hashUserIdToColor(
							el.initiated_by_uid
						),
					};
				}),
			});

			const allowDefaultRun = selectedUser
				? selectedUser.is_default_run
				: false;

			const currentUser = run.users.find(
				(userObj: any) =>
					userObj.initiated_by_uid === uid &&
					userObj.is_default_run === allowDefaultRun
			) ?? run.users.find(
				(userObj: any) => userObj.initiated_by_uid === uid
			) ?? run.users.find(
				(userObj: any) => userObj.is_default_run
			) ?? run.users[0];

			obj.runId = currentUser?.run_id || "";

			return obj;
		});
		setModelOptions(runs);
		setSelectedRun((previous) =>
			runs.find(
				(run) => run.runCombinationId === previous?.runCombinationId
			) ?? runs[0] ?? null
		);
		setSelectedUID(uid);
		setUserList(userList);
	}, [valuationRun, user, selectedUser]);

	const [
		isSimulationDrawerOpen,
		setIsSimulationDrawerOpen,
	] = useState(false);

	const value = {
		tapeId,
		isDatasetValuation,
		selectedRun,
		setSelectedRun,
		selectedUID,
		setSelectedUID,
		selectedPoint,
		setSelectedPoint,
		modelOptions,
		userList,
		selectedUser,
		setSelectedUser,
		handlePointSelection,
		actionOptions,
		selectedAction,
		setSelectedAction,
		isEditMaestroDrawerOpen,
		setIsEditMaestroDrawerOpen,
		resetPointData,
		isRunEditable,
		isReadOnlyUser,
		reloadPointData,
		isSimulationDrawerOpen,
		setIsSimulationDrawerOpen,
	};
	return (
		<AnalysisContext.Provider value={value}>
			{children}
		</AnalysisContext.Provider>
	);
};
